import { useEffect, useState } from "react";
import { Plus, Loader2, Trash2, X } from "lucide-react";
import api, { apiErrorMessage } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { StatusBadge } from "../../components/StatusBadge";

export default function AdminFronts() {
  const { sites, selectedSite } = useAuth();
  const [fronts, setFronts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ front_libelle: "", ordre_geographique: 0, site: "" });
  const [busy, setBusy] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteError, setDeleteError] = useState("");

  const load = () => api.get(`/fronts?sort=geo&site=${selectedSite}`).then(({ data }) => setFronts(data));
  useEffect(() => { load(); }, [selectedSite]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (sites.length && !form.site) setForm((f) => ({ ...f, site: sites[0] }));
  }, [sites]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/admin/fronts", form);
      setForm({ front_libelle: "", ordre_geographique: 0, site: sites[0] || "" });
      setShowForm(false);
      load();
    } finally { setBusy(false); }
  };

  const remove = async (frontId) => {
    setBusy(true);
    setDeleteError("");
    try {
      await api.delete(`/admin/fronts/${frontId}`);
      setDeletingId(null);
      load();
    } catch (e) {
      setDeleteError(apiErrorMessage(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button data-testid="admin-fronts-add-button" onClick={() => setShowForm(!showForm)} className="bg-cyan-500 hover:bg-cyan-400 text-black font-bold uppercase text-xs">
          <Plus className="w-4 h-4 mr-1.5" /> Ajouter un front
        </Button>
      </div>
      {showForm && (
        <div className="rounded-xl border border-slate-800 bg-[#121820] p-4 grid sm:grid-cols-3 gap-3">
          <Input data-testid="admin-front-libelle-input" placeholder="Ex: Front 347.5-355" value={form.front_libelle} onChange={(e) => setForm({ ...form, front_libelle: e.target.value })} className="bg-[#0A0D12] border-slate-700 text-slate-100 sm:col-span-2" />
          <Input data-testid="admin-front-ordre-input" type="number" placeholder="Ordre géo." value={form.ordre_geographique} onChange={(e) => setForm({ ...form, ordre_geographique: e.target.value })} className="bg-[#0A0D12] border-slate-700 text-slate-100" />
          <select data-testid="admin-front-site-select" value={form.site} onChange={(e) => setForm({ ...form, site: e.target.value })} className="bg-[#0A0D12] border border-slate-700 rounded-md h-10 px-3 text-sm text-slate-100 sm:col-span-3">
            {sites.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <Button data-testid="admin-front-submit-button" disabled={busy || !form.front_libelle} onClick={create} className="sm:col-span-3 bg-emerald-500 hover:bg-emerald-400 text-black font-bold uppercase text-xs">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Créer"}
          </Button>
        </div>
      )}
      <div className="space-y-2">
        {fronts.map((f) => (
          <div key={f.Front_ID} data-testid={`admin-front-row-${f.Front_ID}`} className="rounded-lg border border-slate-800 bg-[#121820] p-3 flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-sm font-semibold text-slate-100">{f.Front_Libelle}</div>
              <div className="text-xs text-slate-500 font-mono">{f.Front_ID} · ordre {f.Ordre_Geographique} · {f.Site_Usine}</div>
            </div>
            <div className="flex items-center gap-2">
              <StatusBadge status={f.etat?.Statut_Global} />
              {deletingId === f.Front_ID ? (
                <>
                  <Button data-testid={`admin-front-delete-confirm-${f.Front_ID}`} disabled={busy} size="sm" onClick={() => remove(f.Front_ID)} className="bg-red-600 hover:bg-red-500 text-white text-xs">
                    Confirmer
                  </Button>
                  <Button data-testid={`admin-front-delete-cancel-${f.Front_ID}`} size="sm" variant="outline" onClick={() => { setDeletingId(null); setDeleteError(""); }} className="border-slate-700 text-xs">
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </>
              ) : (
                <Button data-testid={`admin-front-delete-${f.Front_ID}`} size="sm" variant="outline" onClick={() => setDeletingId(f.Front_ID)} className="border-red-900 text-red-400 hover:bg-red-950 text-xs">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
      {deleteError && <div data-testid="admin-front-delete-error" className="text-sm text-red-400 bg-red-950/40 border border-red-900 rounded-lg px-3 py-2">{deleteError}</div>}
    </div>
  );
}
